import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Film, Loader2, Upload, Clapperboard, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { errMsg } from "./mk";
import { uploadMedia, waitJob, reelKey } from "./mkMedia";
import { MusicPicker } from "./MusicPicker";

const VideoPicker = ({ video, onVideo }) => {
  const ref = useRef(null);
  const [pct, setPct] = useState(null);
  const pick = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error("Video is too large (max 100 MB)"); return; }
    setPct(0);
    try { const v = await uploadMedia(file, "video", setPct); onVideo({ ...v, local: URL.createObjectURL(file) }); toast.success("Video uploaded"); }
    catch (e) { toast.error(errMsg(e, "Video upload failed")); }
    finally { setPct(null); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid="video-picker">
      <div className="flex items-center gap-2"><Film className="h-4 w-4 text-blue-700" /><span className="font-semibold text-sm">Video</span><span className="text-[11px] text-slate-500">MP4 or MOV · up to 100 MB · 3–90 seconds</span></div>
      <input ref={ref} type="file" accept="video/mp4,video/quicktime,.mp4,.mov,.m4v" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="video-file-input" />
      {pct !== null ? <div className="flex items-center gap-2 text-sm text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Uploading {pct}%</div> : (
        <div className="flex items-center gap-2 flex-wrap">
          <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} data-testid="video-upload-btn"><Upload className="mr-1 h-4 w-4" /> {video ? "Replace video" : "Upload video"}</Button>
          {video && <span className="text-xs text-slate-600" data-testid="video-info">{video.filename} · {Math.round(video.duration)}s{video.duration > 90 ? " (first 90s used)" : ""}{video.has_audio ? "" : " · no sound"}</span>}
        </div>
      )}
    </div>
  );
};

export const ReelBuilder = ({ reel, imagePath, onReel, cache, setCache }) => {
  const [busy, setBusy] = useState(false);
  const set = (patch) => onReel({ ...reel, ...patch });
  const isPhoto = reel.source === "photo";
  const current = reelKey(reel, imagePath);
  const stale = reel.video_path && reel.rendered_key !== current;
  useEffect(() => {
    if (reel.music_id && !cache.music) api.get(`/admin/marketing/media/${reel.music_id}`).then((r) => setCache((c) => ({ ...c, music: r.data }))).catch(() => {});
    if (reel.video_id && !cache.video) api.get(`/admin/marketing/media/${reel.video_id}`).then((r) => setCache((c) => ({ ...c, video: r.data }))).catch(() => {});
  }, [reel.music_id, reel.video_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const make = async () => {
    if (isPhoto && !imagePath) { toast.error("Choose or make a poster/photo below first"); return; }
    if (isPhoto && !reel.music_id) { toast.error("Add music first"); return; }
    if (!isPhoto && !reel.video_id) { toast.error("Upload a video first"); return; }
    setBusy(true);
    try {
      const body = { ...reel, image_path: isPhoto ? imagePath : "" };
      const { data } = await api.post("/admin/marketing/render", body);
      const j = await waitJob(data.job_id);
      if (j.status !== "done") throw new Error(j.error || "Reel could not be made");
      onReel({ ...reel, image_path: body.image_path, video_path: j.video_path, cover_path: j.cover_path, sig: j.sig, has_music: j.has_music, rendered_key: current });
      toast.success("Reel preview ready — check it on the right");
    } catch (e) { toast.error(e.message && !e.response ? e.message : errMsg(e, "Reel could not be made")); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4 min-w-0" data-testid="reel-builder">
      <div className="flex items-center gap-2"><Clapperboard className="h-4 w-4 text-pink-600" /><h3 className="font-bold text-slate-900">{isPhoto ? "Photo + music Reel" : "Video Reel"}</h3>
        <span className="text-[11px] text-slate-500">Upright 9:16 · 1080×1920</span></div>
      {!isPhoto && <VideoPicker video={cache.video} onVideo={(v) => { setCache((c) => ({ ...c, video: v })); set({ video_id: v.id }); }} />}
      {!isPhoto && cache.video?.has_audio && (
        <div className="rounded-xl border border-slate-200 p-3 space-y-2">
          <label className="flex items-center gap-2 text-sm"><Switch checked={reel.keep_original} onCheckedChange={(v) => set({ keep_original: v })} data-testid="reel-keep-original" /> Keep the video's own sound</label>
          {reel.keep_original && <div><div className="text-xs text-slate-600 mb-1">Video sound volume: {Math.round(reel.original_volume * 100)}%</div>
            <Slider min={0} max={1} step={0.05} value={[Math.min(1, reel.original_volume)]} onValueChange={([v]) => set({ original_volume: v })} data-testid="reel-original-volume" /></div>}
        </div>
      )}
      <MusicPicker reel={reel} music={cache.music} onMusic={(m) => setCache((c) => ({ ...c, music: m }))} onChange={set} />
      {isPhoto && (
        <div><div className="text-xs text-slate-600 mb-1">Reel length: {reel.duration} seconds</div>
          <Slider min={5} max={60} step={1} value={[reel.duration]} onValueChange={([v]) => set({ duration: v })} data-testid="reel-duration-slider" /></div>
      )}
      <Button type="button" onClick={make} disabled={busy} className="w-full sm:w-auto bg-pink-600 hover:bg-pink-700 text-white" data-testid="reel-make-btn">
        {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Making Reel… (up to 2 min)</> : <><Clapperboard className="mr-1 h-4 w-4" /> {reel.video_path ? "Make Reel preview again" : "Make Reel preview"}</>}
      </Button>
      {stale && <p className="text-xs text-amber-700 flex items-center gap-1" data-testid="reel-stale"><AlertTriangle className="h-3.5 w-3.5" /> Settings changed — make the preview again before saving for approval.</p>}
      {reel.video_path && !stale && <p className="text-xs text-emerald-700 flex items-center gap-1" data-testid="reel-ready"><CheckCircle2 className="h-3.5 w-3.5" /> Reel preview is up to date{reel.has_music ? " · music is mixed into the video" : ""}.</p>}
    </div>
  );
};
